/**
 * Dakar cap offer: the View offer dialog on cap product pages and the one-tap Add on offer cards.
 *
 * Adding goes through Horizon's own path: POST to the cart add route with the cart-items section ids, then a
 * CartAddEvent carrying the returned sections, so the cart drawer and cart page re-render (and auto-open) exactly as
 * they do for the main Add to cart button. The offer's unlocked state and rail live inside those sections, so they
 * update with the cart. This file never applies a discount: pricing is Shopify's.
 */
import { CartAddEvent } from '@theme/events';

if (!window.__dzOfferBound) {
  window.__dzOfferBound = true;

  const track = (name, detail) => {
    try {
      window.dzTrack?.(name, detail);
    } catch {}
  };

  // The offer gives one Dakar reward per qualifying cap. Shopify's Buy X Get Y enforces that in the cart, but it does
  // it silently and by discounting the cheapest eligible line, so a shopper who adds more Dakar items than caps would
  // see the discount land on something they did not choose. The picker therefore refuses the extra add in the first
  // place, which keeps what the card promises and what checkout charges the same thing.
  let sets = null;
  function offerSets() {
    if (sets) return sets;
    const node = document.querySelector('[data-dz-offer-sets]');
    try {
      const parsed = JSON.parse(node?.textContent || '{}');
      sets = { caps: new Set(parsed.caps || []), rewards: new Set(parsed.rewards || []) };
    } catch {
      sets = { caps: new Set(), rewards: new Set() };
    }
    return sets;
  }

  /**
   * How many more Dakar rewards the cart has earned. Null when the sets are missing, meaning "do not block".
   *
   * @param {string} [pendingCapId] - product id of a qualifying cap whose own page this is. On that page the shopper
   *   is about to buy the cap, so it counts once even before it reaches the cart; if it is already in the cart the
   *   loop below has counted it and it must not be counted twice.
   */
  async function rewardBudget(pendingCapId) {
    const { caps, rewards } = offerSets();
    if (!caps.size || !rewards.size) return null;
    try {
      const cartUrl = `${(window.Theme?.routes?.cart_url || '/cart').replace(/\/$/, '')}.js`;
      const cart = await (await fetch(cartUrl, { headers: { Accept: 'application/json' } })).json();
      let earned = 0;
      let taken = 0;
      let pendingInCart = false;
      for (const item of cart.items || []) {
        if (caps.has(item.handle)) {
          earned += item.quantity;
          if (pendingCapId && String(item.product_id) === String(pendingCapId)) pendingInCart = true;
        } else if (rewards.has(item.handle)) {
          taken += item.quantity;
        }
      }
      if (pendingCapId && !pendingInCart) earned += 1;
      return earned - taken;
    } catch {
      return null;
    }
  }

  /** The cap whose page a card is being shown on, if any: the offer dialog only exists on qualifying cap pages. */
  function pendingCapFor(card) {
    return card?.closest('[data-dz-offer-dialog]')?.dataset.productId || '';
  }

  /** Locks or unlocks every offer card on the page against the current budget. */
  async function refreshBudget() {
    const cards = [...document.querySelectorAll('[data-dz-offer-card]')];
    const pending = cards.map(pendingCapFor).find(Boolean) || '';
    const budget = await rewardBudget(pending);
    if (budget === null) return;
    const locked = budget <= 0;
    for (const card of cards) {
      const button = card.querySelector('[data-dz-offer-add]');
      const notice = card.querySelector('[data-dz-offer-error]');
      if (!(button instanceof HTMLButtonElement)) continue;
      button.disabled = locked;
      if (locked) {
        if (notice && notice.hidden) {
          notice.textContent = button.dataset.needsCap || '';
          notice.hidden = !notice.textContent;
        }
      } else if (notice) {
        notice.hidden = true;
      }
    }
  }

  /** @param {HTMLDialogElement} dialog */
  async function loadDialogProducts(dialog) {
    if (dialog.dataset.loaded === 'true' || dialog.dataset.loading === 'true') return;
    const grid = dialog.querySelector('[data-dz-offer-grid]');
    const status = dialog.querySelector('[data-dz-offer-status]');
    const sections = (dialog.dataset.sections || '').split(',').filter(Boolean);
    if (!grid || !sections.length) return;

    dialog.dataset.loading = 'true';
    try {
      const root = dialog.dataset.root || '/';
      const base = `${root}${root.endsWith('/') ? '' : '/'}`;
      // One request per page: Shopify's 20-handle all_products budget is per request, not per section, so asking
      // for several pages at once renders only the first 20 products.
      const pages = await Promise.all(
        sections.map(async (id) => {
          const response = await fetch(`${base}?sections=${encodeURIComponent(id)}`, { headers: { Accept: 'application/json' } });
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          const data = await response.json();
          return data[id] || '';
        })
      );
      const html = pages.join('');
      const template = document.createElement('template');
      template.innerHTML = html;
      // Some Dakar caps are themselves on the offer list. On that cap's own page it is the cap being bought, which
      // stays at its normal price, so it is not listed as a discounted item.
      if (dialog.dataset.productId) {
        template.content
          .querySelectorAll(`[data-dz-offer-card][data-product-id="${CSS.escape(dialog.dataset.productId)}"]`)
          .forEach((card) => card.remove());
      }
      const lists = template.content.querySelectorAll('.dz-offer-list');
      if (!lists.length || !template.content.querySelector('[data-dz-offer-card]')) throw new Error('empty');
      // Each page arrives as its own grid; one grid avoids a part-filled row at every page boundary.
      const [first, ...rest] = lists;
      rest.forEach((list) => first.append(...list.children));
      grid.replaceChildren(first);
      dialog.dataset.loaded = 'true';
    } catch (error) {
      if (status) status.textContent = status.dataset.error || '';
    } finally {
      grid.setAttribute('aria-busy', 'false');
      delete dialog.dataset.loading;
    }
  }

  /** @param {HTMLElement} card */
  function selectedOption(card) {
    const field = card.querySelector('[data-dz-offer-variant]');
    if (field instanceof HTMLSelectElement) {
      const option = field.selectedOptions[0] || null;
      return { id: field.value, rate: option?.dataset.rate || '' };
    }
    if (field instanceof HTMLInputElement) return { id: field.value, rate: field.dataset.rate || '' };
    return { id: '', rate: '' };
  }

  class CartError extends Error {}

  /** @param {HTMLElement} card @param {HTMLButtonElement} button */
  async function addToCart(card, button) {
    const { id, rate } = selectedOption(card);
    const error = card.querySelector('[data-dz-offer-error]');
    if (!id || button.getAttribute('aria-busy') === 'true') return;

    button.setAttribute('aria-busy', 'true');
    button.textContent = button.dataset.busyLabel || button.textContent;
    if (error) error.hidden = true;

    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : ''))
      .filter(Boolean);

    try {
      // Re-check against the live cart: another tab, the cart drawer or a back-button restore can change it between
      // render and click.
      const budget = await rewardBudget(pendingCapFor(card));
      if (budget !== null && budget <= 0) {
        if (error) {
          error.textContent = button.dataset.needsCap || '';
          error.hidden = !error.textContent;
        }
        button.disabled = true;
        return;
      }
      const response = await fetch(window.Theme?.routes?.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: Number(id), quantity: 1 }],
          sections: sectionIds.join(','),
          sections_url: window.location.pathname,
        }),
      });
      const data = await response.json();
      // Shopify's own cart errors (for example, not enough stock) come back already in the storefront language.
      if (!response.ok || data.status) throw new CartError(data.description || data.message || '');

      track('dz_offer_add', {
        dz_source: card.closest('[data-dz-offer-cart]') ? 'cart' : 'offer_dialog',
        dz_rate: rate,
      });

      // cart-icon.js treats itemCount from any source other than the product form as the cart's total, so send the
      // real total rather than the one unit just added.
      let itemCount = 1;
      try {
        const cartUrl = `${(window.Theme?.routes?.cart_url || '/cart').replace(/\/$/, '')}.js`;
        const cart = await (await fetch(cartUrl, { headers: { Accept: 'application/json' } })).json();
        if (typeof cart.item_count === 'number') itemCount = cart.item_count;
      } catch {}

      const dialog = card.closest('dialog[data-dz-offer-dialog]');
      if (dialog instanceof HTMLDialogElement && dialog.open) dialog.close();

      refreshBudget();
      document.dispatchEvent(
        new CartAddEvent({}, `dz-offer-${id}`, {
          source: 'dz-offer',
          itemCount,
          variantId: String(id),
          productId: card.dataset.productId,
          sections: data.sections,
        })
      );
    } catch (failure) {
      if (error) {
        // Never show a raw technical message: use Shopify's localised cart error when there is one, otherwise the
        // theme's own translated message.
        const shopifyMessage = failure instanceof CartError ? failure.message : '';
        error.textContent = shopifyMessage || button.dataset.error || '';
        error.hidden = false;
      }
    } finally {
      if (button.isConnected) {
        button.removeAttribute('aria-busy');
        button.textContent = button.dataset.label || button.textContent;
      }
    }
  }

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const open = target.closest('[data-dz-offer-open]');
    if (open) {
      // The button and its dialog share a root: the cap page's promo, or the cart panel (page or drawer).
      const promo = open.closest('[data-dz-offer-root]');
      // The cart panel holds its dialog; the cap page's promo has it as the next sibling.
      const sibling = promo?.nextElementSibling;
      const dialog =
        promo?.querySelector('dialog[data-dz-offer-dialog]') ||
        (sibling instanceof HTMLDialogElement && sibling.matches('[data-dz-offer-dialog]') ? sibling : null);
      const wasInside = !!dialog && promo.contains(dialog);
      if (promo && dialog instanceof HTMLDialogElement) {
        // In quick add the promo sits inside Horizon's own modal dialog, which closes on Escape and hides everything in
        // it. Open the offer from the body instead, and put it back beside its promo when it closes.
        if (dialog.parentElement?.closest('dialog')) {
          document.body.append(dialog);
          dialog.addEventListener(
            'close',
            () => {
              if (!promo.isConnected) dialog.remove();
              else if (wasInside) promo.append(dialog);
              else promo.after(dialog);
            },
            { once: true }
          );
        }
        dialog.showModal();
        loadDialogProducts(dialog).then(refreshBudget);
        track('dz_offer_dialog_open', { dz_surface: promo.matches('[data-dz-offer-cart]') ? 'cart' : 'product_page' });
      }
      return;
    }

    const close = target.closest('[data-dz-offer-close]');
    if (close) {
      close.closest('dialog')?.close();
      return;
    }

    if (target instanceof HTMLDialogElement && target.matches('[data-dz-offer-dialog]')) {
      // A click on the backdrop lands on the dialog element itself.
      target.close();
      return;
    }

    const add = target.closest('[data-dz-offer-add]');
    if (add instanceof HTMLButtonElement) {
      const card = add.closest('[data-dz-offer-card]');
      if (card instanceof HTMLElement) addToCart(card, add);
    }
  });

  document.addEventListener('change', (event) => {
    const select = event.target;
    if (!(select instanceof HTMLSelectElement) || !select.matches('[data-dz-offer-variant]')) return;
    const card = select.closest('[data-dz-offer-card]');
    const option = select.selectedOptions[0];
    if (!card || !option) return;
    const was = card.querySelector('[data-dz-offer-was]');
    const now = card.querySelector('[data-dz-offer-now]');
    const badge = card.querySelector('[data-dz-offer-badge]');
    if (was && option.dataset.was) was.textContent = option.dataset.was;
    if (now && option.dataset.now) now.textContent = option.dataset.now;
    if (badge && option.dataset.badge) badge.textContent = option.dataset.badge;
  });

  // The cart rail re-renders on every cart change, so re-evaluate the budget whenever the theme announces one.
  refreshBudget();
  document.addEventListener('CartUpdateEvent', refreshBudget);
  document.addEventListener('cart:update', refreshBudget);
}
